import React from 'react';
import { Alert, Card, Space, Statistic, Table, Tag } from 'antd';
import TimezoneAwareTime from '../../components/TimezoneAwareTime';
import { NonStdConfirmSnapshot, NonStdPickSnapshot, NonStdRecordSnapshot, NonStdSnapshot } from '../../types/nonstandardOps';
import { DestroyRecordsTable } from './DestroyRecordsPanel';
import EntryTimeline from './EntryTimeline';
import NonStdPickSnapshotView from './NonStdPickSnapshotView';
import { ConsumedTag, LabelGroupSections, consumedRowStyle, hasSizeInfo, sizeWeightCell } from './labelGroups';

interface Props {
  snapshot: NonStdSnapshot;
  /** 指令的操作对象明细（instruction.targets_detail）：拣货快照里缺目标数量时用它兜底 */
  targets?: any[];
}

const CONTAINER_STATUS: Record<string, { text: string; color?: string }> = {
  placed: { text: '已就位', color: 'green' },
  unused: { text: '未使用' },
  consumed: { text: '已消耗' },
  pending: { text: '待处理', color: 'orange' },
};

const Header: React.FC<{ snapshot: NonStdSnapshot; title: string }> = ({ snapshot, title }) => (
  <Alert
    type="info"
    showIcon
    style={{ marginBottom: 12 }}
    message={(
      <span>
        {title}（只读）：提交人 {snapshot.submitted_by_name || '-'}，提交时间{' '}
        <TimezoneAwareTime time={snapshot.submitted_at} format="YYYY-MM-DD HH:mm" showTimezone={false} />
        。提交后的内容已固化，之后不能再修改。
      </span>
    )}
  />
);

const RecordView: React.FC<{ snapshot: NonStdRecordSnapshot }> = ({ snapshot }) => (
  <div>
    <Header snapshot={snapshot} title="执行记录快照" />
    <Space size={32} wrap style={{ marginBottom: 12 }}>
      <Statistic title="执行记录" value={snapshot.counts?.entries ?? 0} suffix="条" />
      <Statistic title="轮次" value={snapshot.counts?.rounds ?? 0} suffix="轮" />
      <Statistic title="容器标签" value={snapshot.counts?.labels ?? 0} suffix="个" />
      {snapshot.voided_count ? <Statistic title="已作废的记录" value={snapshot.voided_count} suffix="条" /> : null}
    </Space>
    <Card size="small" title="执行记录" style={{ marginBottom: 12 }}>
      <EntryTimeline entries={snapshot.entries as any} editable={false} showInternal onChanged={() => undefined} />
    </Card>
    {(snapshot.labels || []).length > 0 ? (
      <Card size="small" title="容器标签" style={{ marginBottom: 12 }}>
        <LabelGroupSections
          rows={snapshot.labels}
          renderTable={rows => (
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={rows}
              onRow={row => ({ style: consumedRowStyle(row) })}
              columns={[
                { title: '编号', dataIndex: 'number', width: 80, render: (v: string) => (v ? <b>{v}</b> : '-') },
                { title: '内容物', dataIndex: 'contents', render: (v: string) => v || '-' },
                { title: '备注', dataIndex: 'remark', render: (v: string) => v || '-' },
                ...(hasSizeInfo(snapshot.labels) ? [{ title: '尺寸/重量', width: 170, render: (_: any, row: any) => sizeWeightCell(row) }] : []),
                { title: '创建人', dataIndex: 'created_by_name', width: 110, render: (v: string) => v || '-' },
                { title: '提交时已打印', dataIndex: 'print_count', width: 110, render: (v: number) => (v > 0 ? `${v} 张` : <Tag>未打印</Tag>) },
                ...(snapshot.labels.some(row => row.consumed) ? [{ title: '状态', width: 80, render: (_: any, row: any) => (row.consumed ? <ConsumedTag /> : '-') }] : []),
              ]}
            />
          )}
        />
      </Card>
    ) : null}
    {(snapshot.destroy_records || []).length > 0 ? (
      <Card size="small" title="销毁记录" style={{ marginBottom: 12 }}>
        <DestroyRecordsTable records={snapshot.destroy_records || []} totals={snapshot.destroy_totals} />
      </Card>
    ) : null}
    {(snapshot.rounds || []).length > 1 || (snapshot.rounds || []).some(r => r.submitted) ? (
      <Card size="small" title="按轮次汇总">
        <Table
          size="small"
          rowKey="round"
          pagination={false}
          dataSource={snapshot.rounds}
          columns={[
            { title: '轮次', dataIndex: 'round', width: 80, render: (v: number) => `第 ${v} 轮` },
            { title: '员工记录数', dataIndex: 'entry_count', width: 100 },
            { title: '其中对客户可见', dataIndex: 'visible_count', width: 120 },
            { title: '客户回复数', dataIndex: 'reply_count', width: 100 },
            { title: '已提交给客户', dataIndex: 'submitted', width: 120, render: (v: boolean) => (v ? '是' : '否') },
          ]}
        />
      </Card>
    ) : null}
  </div>
);

const ConfirmView: React.FC<{ snapshot: NonStdConfirmSnapshot }> = ({ snapshot }) => {
  const counts = snapshot.counts;
  return (
    <div>
      <Header snapshot={snapshot} title="确认完成快照" />
      <Space size={32} wrap style={{ marginBottom: 12 }}>
        <Statistic title="容器" value={counts?.total ?? 0} suffix="个" />
        <Statistic title="已就位" value={counts?.placed ?? 0} />
        <Statistic title="未使用" value={counts?.unused ?? 0} />
        {(counts?.consumed ?? 0) > 0 ? <Statistic title="已消耗" value={counts?.consumed ?? 0} /> : null}
      </Space>
      <Card size="small" title="补充说明" style={{ marginBottom: 12 }}>
        {snapshot.note ? <div style={{ whiteSpace: 'pre-wrap' }}>{snapshot.note}</div> : <span style={{ color: '#8c8c8c' }}>没有补充说明</span>}
      </Card>
      {(snapshot.destroy_records || []).length > 0 ? (
        <Card size="small" title="销毁记录" style={{ marginBottom: 12 }}>
          <DestroyRecordsTable records={snapshot.destroy_records || []} totals={snapshot.destroy_totals} />
        </Card>
      ) : snapshot.no_destroy_declared ? (
        <Card size="small" title="销毁记录" style={{ marginBottom: 12 }}>
          <span className="nonstd-snapshot-no-destroy">
            已声明本指令没有发生销毁：{snapshot.no_destroy_by_name || '-'}
            {snapshot.no_destroy_at ? <>，<TimezoneAwareTime time={snapshot.no_destroy_at} format="YYYY-MM-DD HH:mm" showTimezone={false} /></> : null}
          </span>
        </Card>
      ) : null}
      <Card size="small" title="容器核对">
        {(snapshot.containers || []).length === 0 ? <span style={{ color: '#8c8c8c' }}>本次没有生成容器</span> : (
          <LabelGroupSections
            rows={snapshot.containers}
            renderTable={rows => (
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={rows}
                onRow={row => ({ style: consumedRowStyle(row) })}
                columns={[
                  { title: '编号', dataIndex: 'number', width: 80, render: (v: string) => (v ? <b>{v}</b> : '-') },
                  { title: '内容物', dataIndex: 'contents', render: (v: string) => v || '-' },
                  { title: '备注', dataIndex: 'remark', render: (v: string) => v || '-' },
                  ...(hasSizeInfo(snapshot.containers) ? [{ title: '尺寸/重量', width: 170, render: (_: any, row: any) => sizeWeightCell(row) }] : []),
                  { title: '已贴标签', dataIndex: 'labeled', width: 90, render: (v: boolean, row: any) => (row.status === 'unused' || row.status === 'consumed' ? '-' : v ? '是' : '否') },
                  { title: '放置位置', dataIndex: 'location', render: (v: string, row: any) => (row.status === 'consumed' ? '-' : v || '-') },
                  {
                    title: '状态',
                    width: 160,
                    render: (_: any, row: any) => {
                      const info = CONTAINER_STATUS[row.status] || { text: row.status };
                      return (
                        <div style={{ fontSize: 12 }}>
                          {row.status === 'consumed' ? <ConsumedTag /> : <Tag color={info.color}>{info.text}</Tag>}
                          {row.status === 'unused' && row.unused_reason ? <div style={{ color: '#8c8c8c' }}>{row.unused_reason}</div> : null}
                        </div>
                      );
                    },
                  },
                ]}
              />
            )}
          />
        )}
      </Card>
    </div>
  );
};

/** 阶段快照（只读）：执行记录 / 确认完成。内容是步骤提交时由后端固化的，之后不再变化。 */
const NonStdSnapshotView: React.FC<Props> = ({ snapshot, targets }) => {
  if (!snapshot) return null;
  if (snapshot.stage === 'record') return <RecordView snapshot={snapshot as NonStdRecordSnapshot} />;
  if (snapshot.stage === 'pick') return <NonStdPickSnapshotView snapshot={(snapshot as NonStdPickSnapshot).data || {}} targets={targets} />;
  return <ConfirmView snapshot={snapshot as NonStdConfirmSnapshot} />;
};

export default NonStdSnapshotView;
